import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import ButtonContent from '../ButtonContent';
import ButtonDivider from '../ButtonDivider';

import backgroundStyles from './style/background.css';
import colorStyles from './style/color.css';
import generalStyles from './style/general.css';
import hoverStyles from './style/hover.css';
import iconColorStyles from './style/iconColor.css';
import iconHoverStyles from './style/iconHover.css';
import positionSizeStyles from './style/positionSize.css';
import positionStyleStyles from './style/positionStyle.css';
import sizeStyles from './style/size.css';
import stateStyles from './style/state.css';
import styleStyles from './style/style.css';

import { buttonSize } from '../sizes.spec';

/**
 * We have a variety of button sizes, colors and types which are all meant to be
 * used in different context.
 */
export default class Button extends Component {
  state = {
    isHovered: false,
  };

  mouseEnter = () => {
    this.setState({ isHovered: true });
  };

  mouseLeave = () => {
    this.setState({ isHovered: false });
  };

  render() {
    const {
      background,
      children,
      color,
      fullWidth,
      hasErrored,
      hasSucceeded,
      hover,
      icon,
      iconColor,
      iconHover,
      isDeactivated,
      isDisabled,
      isIconCentered,
      isLoading,
      isReversed,
      isSad,
      isStateful,
      kind,
      minWidth,
      name,
      onClick,
      position,
      shape,
      size,
      style,
      tabIndex,
      type,
    } = this.props;

    // Depreciated style prop
    if (
      process.env.NODE_ENV !== 'production' &&
      this.props.style &&
      !Button.didWarn
    ) {
      Button.didWarn = true;
      // eslint-disable-next-line no-console
      console.warn(
        'Warning: Button.style is deprecated. Use Button.kind instead'
      );
    }

    const kindProp = typeof style === 'undefined' ? kind : style;

    const { isHovered } = this.state;

    // Need to add this check because a lot of the
    // buttons that we currently use will break
    let colorStyle;
    const isInState = !isDisabled && !isReversed && !isLoading;

    if (kindProp === 'flat' && color === undefined && isInState) {
      colorStyle = colorStyles.white;
    } else if (kindProp === 'flat' && color === undefined && isInState) {
      colorStyle = colorStyles.slate;
    } else if (isInState) {
      colorStyle = colorStyles[color];
    }

    const buttonClasses = cx(
      generalStyles.button,
      hoverStyles[hover],
      positionSizeStyles[`${position}-${size}`],
      positionStyleStyles[`${position}-${kindProp}`],
      sizeStyles[size],
      colorStyle,
      {
        [backgroundStyles[background]]: !isDisabled && !isLoading,
        [generalStyles.circularTransition]: isSad,
        [generalStyles.fullWidth]: fullWidth,
        [generalStyles.isIconCentered]: isIconCentered,
        [generalStyles.linearTransition]: !isSad,
        [iconColorStyles.silver]: isDisabled,
        [iconColorStyles[iconColor]]: !isDisabled,
        [iconHoverStyles[iconHover]]: isHovered && !isDisabled,
        [stateStyles.isDeactivated]: isDeactivated || hasSucceeded,
        [stateStyles.isDisabled]: isDisabled && !isReversed,
        [stateStyles.isDisabledReversed]: isDisabled && isReversed,
        [stateStyles.isLoading]: isLoading && !isReversed,
        [stateStyles.isLoadingReversed]: isLoading && isReversed,
        [stateStyles.isReversed]: isReversed && !isDisabled,
        [styleStyles[kindProp]]: !isDisabled && !isLoading,
      }
    );

    const baseStyle = {
      height: buttonSize[size],
      lineHeight: !isIconCentered && `${buttonSize[size]}px`,
      minWidth: !minWidth ? buttonSize[size] : minWidth,
    };

    const spacingCondition =
      (!isIconCentered && (isLoading || icon)) || (isHovered && isSad);

    const spacingStyle = children && {
      paddingLeft: spacingCondition ? buttonSize[size] : buttonSize[size] / 2,
      paddingRight: buttonSize[size] / 2,
    };

    let borderRadius;

    if (position === 'left') {
      borderRadius = `${buttonSize[size] / 2}px 0 0 ${buttonSize[size] / 2}px`;
    } else if (position === 'right') {
      borderRadius = `0 ${buttonSize[size] / 2}px ${buttonSize[size] / 2}px 0`;
    } else if (position === 'solo') {
      borderRadius = buttonSize[size] / 2;
    } else {
      borderRadius = 0;
    }

    const borderRadiusStyle = shape === 'rounded' && {
      borderRadius,
    };

    const buttonStyle = {
      ...baseStyle,
      ...borderRadiusStyle,
      ...spacingStyle,
    };

    const buttonProps = {
      className: buttonClasses,
      disabled: isDisabled,
      name,
      onClick,
      onMouseEnter: this.mouseEnter,
      onMouseLeave: this.mouseLeave,
      style: buttonStyle,
      tabIndex,
      type,
    };

    const buttonContentProps = {
      fullWidth,
      hasErrored,
      hasSucceeded,
      icon,
      isHovered,
      isIconCentered,
      isLoading,
      isSad,
      isStateful,
      size,
    };

    const buttonGroup = position !== 'right' && position !== 'solo';
    const isActive = isHovered || kindProp === 'flat';

    if (type === 'button' || 'reset' || 'submit') {
      return (
        <button {...buttonProps}>
          <ButtonContent {...buttonContentProps}>{children}</ButtonContent>
          {buttonGroup && <ButtonDivider hover={hover} isActive={isActive} />}
        </button>
      );
    }

    return (
      <div role="button" {...buttonProps}>
        <ButtonContent {...buttonContentProps}>{children}</ButtonContent>
        {buttonGroup && <ButtonDivider hover={hover} isHovered={isHovered} />}
      </div>
    );
  }
}

Button.propTypes = {
  background: PropTypes.oneOf(Object.keys(backgroundStyles)),
  children: PropTypes.node,
  color: PropTypes.oneOf(Object.keys(colorStyles)),
  fullWidth: PropTypes.bool,
  hasErrored: PropTypes.bool,
  hasSucceeded: PropTypes.bool,
  hover: PropTypes.oneOf(Object.keys(hoverStyles)),
  icon: PropTypes.node,
  iconColor: PropTypes.oneOf(Object.keys(iconColorStyles)),
  iconHover: PropTypes.oneOf(Object.keys(iconHoverStyles)),
  isDeactivated: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isIconCentered: PropTypes.bool,
  isLoading: PropTypes.bool,
  isReversed: PropTypes.bool,
  isSad: PropTypes.bool,
  isStateful: PropTypes.bool,
  kind: PropTypes.oneOf(Object.keys(styleStyles)),
  minWidth: PropTypes.number,
  name: PropTypes.node,
  onClick: PropTypes.func,
  padding: PropTypes.node,
  position: PropTypes.oneOf(['left', 'right', 'middle', 'solo']),
  shape: PropTypes.oneOf(['rounded', 'normal']),
  size: PropTypes.oneOf(Object.keys(sizeStyles)),
  style: PropTypes.oneOf(Object.keys(styleStyles)),
  tabIndex: PropTypes.number,
  type: PropTypes.oneOf(['button', 'reset', 'submit']),
};

Button.defaultProps = {
  background: 'blue',
  children: null,
  color: undefined,
  fullWidth: false,
  hasErrored: false,
  hasSucceeded: false,
  hover: 'blue',
  icon: null,
  iconColor: 'extraDarkSmoke',
  iconHover: 'white',
  isDeactivated: false,
  isDisabled: false,
  isIconCentered: false,
  isLoading: false,
  isReversed: false,
  isSad: false,
  isStateful: false,
  kind: 'flat',
  position: 'solo',
  shape: 'normal',
  size: 'medium',
  type: 'button',
};



// WEBPACK FOOTER //
// ./packages/marvel-ui-button/Button/index.js